import { useState, useEffect, useCallback } from 'react';

interface OfflineStatus {
  isOffline: boolean;
  wasOffline: boolean;
  lastOnlineAt: Date | null;
  connectionType: string | null;
}

export function useOfflineStatus() {
  const [status, setStatus] = useState<OfflineStatus>(() => ({
    isOffline: typeof navigator !== 'undefined' ? !navigator.onLine : false,
    wasOffline: false,
    lastOnlineAt: null,
    connectionType: null,
  }));

  const updateConnectionInfo = useCallback(() => {
    // @ts-ignore - Navigator connection API
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection) {
      setStatus(prev => ({
        ...prev,
        connectionType: connection.effectiveType || connection.type || null,
      }));
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setStatus(prev => ({
        ...prev,
        isOffline: false,
        wasOffline: true,
        lastOnlineAt: new Date(),
      }));
      updateConnectionInfo();
    };

    const handleOffline = () => {
      setStatus(prev => ({
        ...prev,
        isOffline: true,
      }));
    };

    // Initial connection info
    updateConnectionInfo();

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen for connection changes
    // @ts-ignore
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection) {
      connection.addEventListener('change', updateConnectionInfo);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (connection) {
        connection.removeEventListener('change', updateConnectionInfo);
      }
    };
  }, [updateConnectionInfo]);

  const clearWasOffline = useCallback(() => {
    setStatus(prev => ({ ...prev, wasOffline: false }));
  }, []);

  return {
    ...status,
    clearWasOffline,
  };
}
